import { authedFetch } from "../auth";
import { API_BASE_URL, ENVIRONMENT_DEV } from "../config";
import { trimTrailingSlashes, trimWrappingDoubleQuotes } from "../utils/safeString";

const ELEVENLABS_API_BASE = trimTrailingSlashes(
  process.env.REACT_APP_ELEVENLABS_API_BASE || "https://api.elevenlabs.io"
);

const ELEVENLABS_AGENT_ID_DEFAULT =
  process.env.REACT_APP_ELEVENLABS_AGENT_ID ||
  "agent_6701kfgvxxtrf2svmqfy9pgh62q9";
const LLM_TESTS_API_BASE = trimTrailingSlashes(
  process.env.REACT_APP_LLM_TESTS_API_URL || "http://localhost:9001"
);

function getLlmTestsApiKey() {
  const key = String(process.env.REACT_APP_LLM_TESTS_API_KEY || "change-me").trim();
  return key || "change-me";
}

function getPublicApiKey() {
  return String(
    process.env.REACT_APP_PUBLIC_API_KEY ||
      process.env.REACT_APP_API_KEY ||
      ""
  ).trim();
}

function buildPublicAuthorizationValue(apiKey) {
  // Public endpoints use app.py auth + CORS policy that allows Authorization header.
  const v = String(apiKey || "").trim();
  if (!v) return "";
  if (/^(apikey|bearer)\s+/i.test(v)) return v;
  return `ApiKey ${v}`;
}

async function apiKeyFetch(url, opts = {}) {
  const apiKey = getPublicApiKey();
  if (!apiKey) {
    throw new Error("Липсва REACT_APP_PUBLIC_API_KEY в .env.local");
  }

  const authorization = buildPublicAuthorizationValue(apiKey);
  const headers = {
    ...(opts.headers || {}),
    Authorization: authorization,
  };

  return fetch(url, { ...opts, headers });
}

function inferDirectionFromFilename(name = "") {
  const s = String(name || "").toLowerCase();
  if (s.includes("cc-que")) return "in";
  if (s.includes("out")) return "out";
  return "out";
}

function getFilenameFromContentDisposition(cdHeader, fallbackName) {
  if (!cdHeader) return fallbackName || "file";

  let filename = fallbackName || "file";

  const starMatch = cdHeader.match(/filename\*=(?:UTF-8'')?([^;]+)/i);
  if (starMatch) {
    let v = starMatch[1].trim();
    v = trimWrappingDoubleQuotes(v);
    try {
      if (/%[0-9A-Fa-f]{2}/.test(v)) {
        v = decodeURIComponent(v);
      }
    } catch { }
    return sanitizeFilenameUnicode(v || filename);
  }

  const normalMatch = cdHeader.match(/filename=([^;]+)/i);
  if (normalMatch) {
    let v = normalMatch[1].trim();
    v = trimWrappingDoubleQuotes(v);
    return sanitizeFilenameUnicode(v || filename);
  }

  return filename;
}

function sanitizeFilenameUnicode(name) {
  const s = String(name || "file");

  const forbidden = '<>:"/\\|?*';

  let out = "";
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    const code = s.charCodeAt(i);

    if (code < 32 || code === 127 || forbidden.includes(ch)) {
      out += "_";
    } else {
      out += ch;
    }
  }

  return out;
}

export async function exportRatingsRange(
  from,
  to,
  typeKey,
  templateId,
  agentId = ""
) {
  const isAllTypes = !typeKey && !templateId;

  if (!isAllTypes && (!typeKey || !templateId)) {
    throw new Error("Избери тип и темплейт за експорт.");
  }

  const q = new URLSearchParams({ from, to });

  if (!isAllTypes) {
    q.append("t", typeKey);
    q.append("template_id", templateId);
  }

  if (agentId) {
    q.append("agent_id", agentId);
  }

  const url = `${API_BASE_URL}/ratings/export-range?${q.toString()}`;
  const res = await authedFetch(url, {
    headers: {
      Accept:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  });

  if (!res.ok) {
    let msg = `Export failed (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) msg = j.detail;
    } catch {
      try {
        msg = await res.text();
      } catch { }
    }
    throw new Error(msg);
  }

  const cd =
    res.headers.get("Content-Disposition") ||
    res.headers.get("content-disposition") ||
    "";
  const fallback = `ratings_${typeKey || "all"}_${from}_${to}.xlsx`;
  const filename = getFilenameFromContentDisposition(cd, fallback);

  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export async function audioUrl(id) {
  const r = await authedFetch(
    `${API_BASE_URL}/audio/${encodeURIComponent(id)}`
  );
  if (!r.ok) throw new Error("Audio URL failed");
  return r.json();
}

export async function exportRatingExcelSilent(id) {
  const res = await authedFetch(
    `${API_BASE_URL}/ratings/${encodeURIComponent(id)}/excel`,
    { method: "GET" }
  );
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`Export failed: ${res.status} ${t}`);
  }
  await res.blob();
}

export async function getRating(id) {
  const r = await authedFetch(`${API_BASE_URL}/ratings/${id}`);
  if (!r.ok) throw new Error("Get rating failed");
  return r.json();
}

export async function saveRating(id, data) {
  const r = await authedFetch(`${API_BASE_URL}/ratings/${id}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!r.ok) throw new Error("Save rating failed");
  return r.json();
}

export async function downloadRatingExcel(id) {
  const res = await authedFetch(`${API_BASE_URL}/ratings/${id}/excel`);
  if (!res.ok) throw new Error("Export excel failed");
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${id}_rating.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function exportRatingExcel(id, suggestedName = null) {
  const res = await authedFetch(`${API_BASE_URL}/ratings/${id}/excel`);
  if (!res.ok) throw new Error("Excel export failed");
  const blob = await res.blob();
  let filename = suggestedName || `${id}_rating.xlsx`;
  const cd =
    res.headers.get("Content-Disposition") ||
    res.headers.get("content-disposition") ||
    "";
  filename = getFilenameFromContentDisposition(cd, filename);

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function upload(file, type = "", agentName = "") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("type", type || "");
  fd.append("agent", agentName || "");

  const dir = inferDirectionFromFilename(file?.name);
  fd.append("direction", dir || "");

  const r = await authedFetch(`${API_BASE_URL}/upload`, {
    method: "POST",
    body: fd,
  });
  if (!r.ok) throw new Error("Upload failed");
  return r.json();
}

export async function summarize(id) {
  const r = await authedFetch(`${API_BASE_URL}/summarize/${id}`, {
    method: "POST",
  });
  if (!r.ok) throw new Error("Summarize failed");
  return r.json();
}

export async function transcript(id) {
  const r = await authedFetch(`${API_BASE_URL}/transcript/${id}`);
  if (!r.ok) throw new Error("Transcript failed");
  return r.json();
}

export async function downloadFile(id, suggestedName = null) {
  const res = await authedFetch(`${API_BASE_URL}/download/${id}`);
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const cd =
    res.headers.get("Content-Disposition") ||
    res.headers.get("content-disposition") ||
    "";
  const ct = (res.headers.get("Content-Type") || "").toLowerCase();

  let fallback;
  if (suggestedName) {
    fallback = suggestedName;
  } else {
    const ext = ct.includes("zip") ? ".zip" : ".txt";
    fallback = `${id}${ext}`;
  }

  const filename = getFilenameFromContentDisposition(cd, fallback);

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function listTemplates(type = null) {
  const q = type ? `?template_type=${encodeURIComponent(type)}` : "";
  const r = await authedFetch(`${API_BASE_URL}/templates${q}`);
  if (!r.ok) throw new Error("List templates failed");
  return r.json();
}

export async function createTemplate(data) {
  const r = await authedFetch(`${API_BASE_URL}/templates`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data || {}),
  });
  if (!r.ok) throw new Error("Create template failed");
  return r.json();
}

export async function updateTemplate(id, patch) {
  const r = await authedFetch(
    `${API_BASE_URL}/templates/${encodeURIComponent(id)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch || {}),
    }
  );
  if (!r.ok) throw new Error("Update template failed");
  return r.json();
}

export async function deleteTemplate(id) {
  const r = await authedFetch(
    `${API_BASE_URL}/templates/${encodeURIComponent(id)}`,
    {
      method: "DELETE",
    }
  );
  if (!r.ok) throw new Error("Delete template failed");
  return r.json();
}

export async function setDefaultTemplate(id) {
  const r = await authedFetch(
    `${API_BASE_URL}/templates/${encodeURIComponent(id)}/set-default`,
    {
      method: "POST",
    }
  );
  if (!r.ok) throw new Error("Set default template failed");
  return r.json();
}

export async function getDefaultTemplate(type) {
  const r = await authedFetch(
    `${API_BASE_URL}/templates/default?t=${encodeURIComponent(type)}`
  );
  if (!r.ok) throw new Error("Get default template failed");
  return r.json();
}

export async function getTemplateUsage(id) {
  const r = await fetch(`/api/templates/${id}/usage`, {
    credentials: "include",
  });
  if (!r.ok) throw new Error("usage failed");
  return r.json();
}

export async function listModels() {
  const r = await authedFetch(`${API_BASE_URL}/models`);
  if (!r.ok) throw new Error("List models failed");
  return r.json();
}

export async function updateModel(modelName, patch) {
  const r = await authedFetch(
    `${API_BASE_URL}/models/${encodeURIComponent(modelName)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch || {}),
    }
  );
  if (!r.ok) throw new Error("Update model failed");
  return r.json();
}

export function getIdTokenPayload() {
  try {
    const raw = localStorage.getItem("kc_tokens");
    if (!raw) return null;
    const [, payload] = raw.split(".");
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

export function hasRealmRole(role) {
  const p = getIdTokenPayload();
  const roles = p?.realm_access?.roles || [];
  return roles.includes(role);
}

export function hasClientRole(clientId, role) {
  const p = getIdTokenPayload();
  const roles = p?.resource_access?.[clientId]?.roles || [];
  return roles.includes(role);
}

export async function listCallTypes() {
  const r = await authedFetch(`${API_BASE_URL}/call-types`);
  if (!r.ok) throw new Error("List call types failed");
  const data = await r.json();
  return (data || []).map((ct) => ({
    code: (ct.code || "").toString().trim().toLowerCase(),
    name: ct.name || ct.code || "",
    description: ct.description || "",
    active: ct.active !== false,
  }));
}

export async function listAgents() {
  const r = await authedFetch(`${API_BASE_URL}/agents`);
  if (!r.ok) throw new Error("List agents failed");
  return r.json();
}

export async function historyPaged({
  page = 1,
  perPage = 12,
  q = "",
  tab = "unrated",
  typeName = "all",
  operator_name = "",
  from = "",
  to = "",
} = {}) {
  const qs = new URLSearchParams();

  qs.set("page", page);
  qs.set("per_page", perPage);

  if (q) qs.set("q", q);
  if (tab === "rated" || tab === "unrated") qs.set("tab", tab);
  if (typeName && typeName !== "all") qs.set("type", typeName);

  if (operator_name) qs.set("operator_name", operator_name);
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);

  const res = await authedFetch(`${API_BASE_URL}/history?${qs.toString()}`);
  if (!res.ok) throw new Error("History failed");

  return res.json();
}

export async function createClientForm(payload) {
  const r = await authedFetch(`${API_BASE_URL}/clients`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload || {}),
  });

  if (!r.ok) {
    let msg = `Create failed (${r.status})`;
    try {
      const j = await r.json();
      if (j?.detail) msg = j.detail;
    } catch {
      try {
        msg = await r.text();
      } catch { }
    }
    throw new Error(msg);
  }

  return r.json();
}

function normalizeClientFormsResponse(data, { page, perPage }) {
  if (Array.isArray(data)) {
    return { items: data, page, perPage, total: data.length };
  }

  return {
    items: data?.items || [],
    page: data?.page ?? page,
    perPage: data?.per_page ?? data?.perPage ?? perPage,
    total: data?.total ?? (data?.items?.length || 0),
    totalPages: data?.total_pages ?? data?.totalPages ?? undefined,
  };
}

export async function listClientForms({
  page = 1,
  perPage = 20,
  q = "",
} = {}) {
  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("per_page", String(perPage));
  if (q) qs.set("q", q);

  const r = await authedFetch(`${API_BASE_URL}/clients?${qs.toString()}`);
  if (!r.ok) throw new Error("List clients failed");

  const data = await r.json();
  return normalizeClientFormsResponse(data, { page, perPage });
}

export async function listClientFormsWithApiKey({
  page = 1,
  perPage = 20,
  q = "",
} = {}) {
  const qs = new URLSearchParams();
  qs.set("page", String(page));
  qs.set("per_page", String(perPage));
  if (q) qs.set("q", q);

  const r = await apiKeyFetch(`${API_BASE_URL}/clients?${qs.toString()}`, {
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(await readErr(r, "List clients failed"));

  const data = await r.json();
  return normalizeClientFormsResponse(data, { page, perPage });
}

export async function getClientForm(id) {
  const r = await authedFetch(`${API_BASE_URL}/clients/${encodeURIComponent(id)}`);
  if (!r.ok) throw new Error("Get client failed");
  return r.json();
}

export async function getClientFormWithApiKey(id) {
  const r = await apiKeyFetch(`${API_BASE_URL}/clients/${encodeURIComponent(id)}`, {
    headers: { Accept: "application/json" },
  });
  if (!r.ok) throw new Error(await readErr(r, "Get client failed"));
  return r.json();
}

export async function updateClientForm(id, patch) {
  const r = await authedFetch(`${API_BASE_URL}/clients/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch || {}),
  });
  if (!r.ok) throw new Error("Update client failed");
  return r.json();
}

export async function deleteClientForm(id) {
  const r = await authedFetch(`${API_BASE_URL}/clients/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!r.ok) throw new Error("Delete client failed");
  return r.json();
}

export async function buildScenarioPrompt({
  client_id = "",
  scenario = "",
} = {}) {
  if (!client_id) {
    throw new Error("buildScenarioPrompt: client_id is required");
  }

  const qs = new URLSearchParams();
  qs.set("client_id", client_id);

  if (scenario) qs.set("scenario", scenario);

  const res = await authedFetch(`${API_BASE_URL}/scenario/build-prompt?${qs.toString()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    let msg = `build-prompt failed (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) msg = j.detail;
    } catch {
      try {
        const t = await res.text();
        if (t) msg = t;
      } catch { }
    }
    throw new Error(msg);
  }

  const data = await res.json();

  const prompt =
    data?.prompt ??
    data?.builtPrompt ??
    data?.built_prompt ??
    data?.data?.prompt ??
    "";
  const first_message =
    data?.first_message ??
    data?.firstMessage ??
    data?.data?.first_message ??
    data?.data?.firstMessage ??
    "";

  return { ...data, prompt, first_message };
}

export async function buildScenarioPromptWithApiKey({
  client_id = "",
  scenario = "",
} = {}) {
  if (!client_id) {
    throw new Error("buildScenarioPromptWithApiKey: client_id is required");
  }

  const qs = new URLSearchParams();
  qs.set("client_id", client_id);
  if (scenario) qs.set("scenario", scenario);

  const res = await apiKeyFetch(`${API_BASE_URL}/scenario/build-prompt?${qs.toString()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    let msg = `build-prompt failed (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) msg = j.detail;
    } catch {
      try {
        const t = await res.text();
        if (t) msg = t;
      } catch {}
    }
    throw new Error(msg);
  }

  const data = await res.json();
  const prompt =
    data?.prompt ??
    data?.builtPrompt ??
    data?.built_prompt ??
    data?.data?.prompt ??
    "";
  const first_message =
    data?.first_message ??
    data?.firstMessage ??
    data?.data?.first_message ??
    data?.data?.firstMessage ??
    "";

  return { ...data, prompt, first_message };
}

function pickId(x) {
  return x?.id || x?._id || "";
}

function normalizeScenario(x, fallbackId = "") {
  const scenario = x && typeof x === "object" ? x : {};
  return {
    ...scenario,
    id: pickId(scenario) || fallbackId || "",
    first_message: scenario?.first_message ?? scenario?.firstMessage ?? "",
  };
}

function pickUserScenarioId(x) {
  return String(
    x?.doc_id ??
      x?.docId ??
      x?.id ??
      x?._id ??
      ""
  ).trim();
}

function normalizeUserScenario(x, fallbackId = "") {
  const scenario = x && typeof x === "object" ? x : {};
  const doc_id = pickUserScenarioId(scenario) || String(fallbackId || "").trim();
  return {
    ...scenario,
    doc_id,
    id: String(scenario?.id ?? doc_id).trim(),
    description: scenario?.description ?? "",
    prompt_template: scenario?.prompt_template ?? scenario?.promptTemplate ?? "",
    active: typeof scenario?.active === "boolean" ? scenario.active : true,
  };
}

function pickUserMoodId(x) {
  return String(
    x?.doc_id ??
      x?.docId ??
      x?.id ??
      x?._id ??
      ""
  ).trim();
}

function normalizeUserMood(x, fallbackId = "") {
  const mood = x && typeof x === "object" ? x : {};
  const doc_id = pickUserMoodId(mood) || String(fallbackId || "").trim();
  return {
    ...mood,
    doc_id,
    id: String(mood?.id ?? doc_id).trim(),
    name: String(mood?.name ?? ""),
    name_norm: String(mood?.name_norm ?? mood?.nameNorm ?? ""),
    description: String(mood?.description ?? ""),
    prompt_fragment: String(
      mood?.prompt_fragment ??
        mood?.promptFragment ??
        mood?.prompt_template ??
        mood?.promptTemplate ??
        ""
    ),
    active: typeof mood?.active === "boolean" ? mood.active : true,
  };
}

function pickLlmTestCaseId(x) {
  return String(
    x?.doc_id ??
      x?.docId ??
      x?.id ??
      x?._id ??
      ""
  ).trim();
}

function normalizeLlmTestCase(x, fallbackId = "") {
  const testCase = x && typeof x === "object" ? x : {};
  const doc_id = pickLlmTestCaseId(testCase) || String(fallbackId || "").trim();
  return {
    ...testCase,
    doc_id,
    id: String(testCase?.id ?? doc_id).trim(),
    name: String(testCase?.name ?? ""),
    description: String(testCase?.description ?? ""),
    assistant_scenario_id: String(
      testCase?.assistant_scenario_id ??
        testCase?.assistantScenarioId ??
        ""
    ).trim(),
    client_id: String(
      testCase?.client_id ??
        testCase?.clientId ??
        ""
    ).trim(),
    user_scenario_id: String(
      testCase?.user_scenario_id ??
        testCase?.userScenarioId ??
        ""
    ).trim(),
    user_mood_id: String(
      testCase?.user_mood_id ??
        testCase?.userMoodId ??
        ""
    ).trim(),
    goal: String(testCase?.goal ?? ""),
    max_turns: Number(testCase?.max_turns ?? testCase?.maxTurns ?? 20),
    max_duration_sec: Number(testCase?.max_duration_sec ?? testCase?.maxDurationSec ?? 300),
    active: typeof testCase?.active === "boolean" ? testCase.active : true,
  };
}

function pickLlmTestRunId(x) {
  return String(
    x?.run_id ??
      x?.runId ??
      x?.id ??
      x?._id ??
      ""
  ).trim();
}

function normalizeLlmTestRun(x, fallbackId = "") {
  const run = x && typeof x === "object" ? x : {};
  const run_id = pickLlmTestRunId(run) || String(fallbackId || "").trim();
  const assistantScenarioResolved =
    run?.assistant_scenario_resolved &&
    typeof run.assistant_scenario_resolved === "object"
      ? run.assistant_scenario_resolved
      : {};
  const transcriptRaw = Array.isArray(run?.transcript) ? run.transcript : [];

  return {
    ...run,
    run_id,
    id: String(run?.id ?? run_id).trim(),
    status: String(run?.status ?? ""),
    assistant_scenario_resolved: {
      id: String(
        assistantScenarioResolved?.id ??
          assistantScenarioResolved?._id ??
          ""
      ).trim(),
      name: String(assistantScenarioResolved?.name ?? ""),
    },
    client_id: String(run?.client_id ?? run?.clientId ?? "").trim(),
    client_name: String(run?.client_name ?? run?.clientName ?? "").trim(),
    user_scenario: String(run?.user_scenario ?? run?.userScenario ?? ""),
    user_mood: String(run?.user_mood ?? run?.userMood ?? ""),
    test_case_id: String(run?.test_case_id ?? run?.testCaseId ?? "").trim(),
    test_case_name: String(run?.test_case_name ?? run?.testCaseName ?? ""),
    goal: String(run?.goal ?? ""),
    started_at: String(run?.started_at ?? run?.startedAt ?? ""),
    ended_at: String(run?.ended_at ?? run?.endedAt ?? ""),
    stop_reason: String(run?.stop_reason ?? run?.stopReason ?? ""),
    summary:
      run?.summary && typeof run.summary === "object"
        ? run.summary
        : {},
    transcript: transcriptRaw.map((turn) => ({
      idx: Number(turn?.idx ?? 0),
      role: String(turn?.role ?? ""),
      text: String(turn?.text ?? ""),
      ts: String(turn?.ts ?? turn?.timestamp ?? ""),
    })),
    error: String(run?.error ?? ""),
  };
}

async function readErr(res, fallback) {
  let msg = fallback || `Request failed (${res.status})`;
  try {
    const j = await res.json();
    if (typeof j?.detail === "string" && j.detail.trim()) {
      msg = j.detail.trim();
    } else if (j?.detail && typeof j.detail === "object") {
      const detailError =
        (typeof j.detail.error === "string" && j.detail.error.trim())
          ? j.detail.error.trim()
          : "";
      const detailMessage =
        (typeof j.detail.message === "string" && j.detail.message.trim())
          ? j.detail.message.trim()
          : "";
      msg = detailError || detailMessage || JSON.stringify(j.detail);
    } else if (typeof j?.message === "string" && j.message.trim()) {
      msg = j.message.trim();
    } else if (j?.detail != null) {
      msg = JSON.stringify(j.detail);
    }
  } catch {
    try {
      const t = await res.text();
      if (t) msg = t;
    } catch { }
  }
  return msg;
}

function normalizeCreditor(x) {
  if (typeof x === "string") {
    return {
      id: "",
      name: x.trim(),
      is_bank: false,
    };
  }

  const creditor = x && typeof x === "object" ? x : {};
  return {
    ...creditor,
    id: String(
      creditor?.id ??
      creditor?._id ??
      creditor?.creditor_id ??
      creditor?.creditorId ??
      ""
    ).trim(),
    name: String(creditor?.name ?? creditor?.creditor_name ?? creditor?.creditorName ?? "").trim(),
    is_bank:
      typeof creditor?.is_bank === "boolean"
        ? creditor.is_bank
        : !!creditor?.isBank,
  };
}

export async function listCreditors() {
  const res = await authedFetch(`${API_BASE_URL}/creditors`);
  if (!res.ok) throw new Error(await readErr(res, "List creditors failed"));

  const data = await res.json();
  const arr = Array.isArray(data) ? data : (data?.items || []);
  return arr
    .map((x) => normalizeCreditor(x))
    .filter((x) => x.name);
}

export async function createCreditor(payload) {
  const body = {
    name: String(payload?.name ?? "").trim(),
    is_bank: typeof payload?.is_bank === "boolean" ? payload.is_bank : !!payload?.isBank,
  };

  const res = await authedFetch(`${API_BASE_URL}/creditors/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readErr(res, "Create creditor failed"));

  const data = await res.json();
  return normalizeCreditor(data);
}

export async function updateCreditor(creditorId, payload) {
  const id = String(creditorId || "").trim();
  if (!id) throw new Error("updateCreditor: creditorId is required");

  const body = {
    name: String(payload?.name ?? "").trim(),
    is_bank: typeof payload?.is_bank === "boolean" ? payload.is_bank : !!payload?.isBank,
  };

  const res = await authedFetch(`${API_BASE_URL}/creditors/edit/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readErr(res, "Update creditor failed"));

  return res.json();
}

export async function deleteCreditor(creditorId) {
  const id = String(creditorId || "").trim();
  if (!id) throw new Error("deleteCreditor: creditorId is required");

  const res = await authedFetch(`${API_BASE_URL}/creditors/delete/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(await readErr(res, "Delete creditor failed"));

  return res.json();
}

export async function listScenarios() {
  const res = await authedFetch(`${API_BASE_URL}/scenarios`);
  if (!res.ok) throw new Error(await readErr(res, "List scenarios failed"));

  const data = await res.json();
  const arr = Array.isArray(data) ? data : (data?.items || []);
  return arr.map((x) => normalizeScenario(x));
}

export async function listScenariosWithApiKey() {
  const res = await apiKeyFetch(`${API_BASE_URL}/scenarios`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(await readErr(res, "List scenarios failed"));

  const data = await res.json();
  const arr = Array.isArray(data) ? data : (data?.items || []);
  return arr.map((x) => normalizeScenario(x));
}

export async function getScenario(id) {
  if (!id) throw new Error("getScenario: id is required");

  const res = await authedFetch(`${API_BASE_URL}/scenarios/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(await readErr(res, "Get scenario failed"));

  const data = await res.json();
  return normalizeScenario(data, id);
}

export async function createScenario(payload) {
  const body = {
    name: (payload?.name || "").trim(),
    prompt: payload?.prompt ?? "",
    first_message: payload?.first_message ?? payload?.firstMessage ?? "",
  };

  const res = await authedFetch(`${API_BASE_URL}/scenarios`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readErr(res, "Create scenario failed"));

  const data = await res.json();
  return normalizeScenario(data);
}

export async function updateScenario(id, patch) {
  if (!id) throw new Error("updateScenario: id is required");

  const body = {
    name: (patch?.name || "").trim(),
    prompt: patch?.prompt ?? "",
    first_message: patch?.first_message ?? patch?.firstMessage ?? "",
  };

  const res = await authedFetch(`${API_BASE_URL}/scenarios/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readErr(res, "Update scenario failed"));

  const data = await res.json();
  return normalizeScenario(data, id);
}

export async function deleteScenario(id) {
  if (!id) throw new Error("deleteScenario: id is required");

  const res = await authedFetch(`${API_BASE_URL}/scenarios/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new Error(await readErr(res, "Delete scenario failed"));

  return res.json();
}

export async function listUserScenarios() {
  const res = await authedFetch(`${API_BASE_URL}/user-scenarios`);
  if (!res.ok) throw new Error(await readErr(res, "List user scenarios failed"));

  const data = await res.json();
  const arr = Array.isArray(data) ? data : (data?.items || []);
  return arr.map((x) => normalizeUserScenario(x));
}

export async function getUserScenario(docId) {
  const id = String(docId || "").trim();
  if (!id) throw new Error("getUserScenario: docId is required");

  const res = await authedFetch(`${API_BASE_URL}/user-scenarios/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(await readErr(res, "Get user scenario failed"));

  const data = await res.json();
  return normalizeUserScenario(data, id);
}

export async function createUserScenario(payload) {
  const body = {
    name: (payload?.name || "").trim(),
    description: String(payload?.description ?? ""),
    prompt_template: String(payload?.prompt_template ?? ""),
    active: typeof payload?.active === "boolean" ? payload.active : true,
  };

  const res = await authedFetch(`${API_BASE_URL}/user-scenarios`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readErr(res, "Create user scenario failed"));

  const data = await res.json();
  return normalizeUserScenario(data);
}

export async function updateUserScenario(docId, payload) {
  const id = String(docId || "").trim();
  if (!id) throw new Error("updateUserScenario: docId is required");

  const body = {
    name: (payload?.name || "").trim(),
    description: String(payload?.description ?? ""),
    prompt_template: String(payload?.prompt_template ?? ""),
    active: typeof payload?.active === "boolean" ? payload.active : true,
  };

  const res = await authedFetch(`${API_BASE_URL}/user-scenarios/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readErr(res, "Update user scenario failed"));

  const data = await res.json();
  return normalizeUserScenario(data, id);
}

export async function listUserMoods() {
  const res = await authedFetch(`${API_BASE_URL}/user-moods`);
  if (!res.ok) throw new Error(await readErr(res, "List user moods failed"));

  const data = await res.json();
  const arr = Array.isArray(data) ? data : (data?.items || []);
  return arr.map((x) => normalizeUserMood(x));
}

export async function getUserMood(docId) {
  const id = String(docId || "").trim();
  if (!id) throw new Error("getUserMood: docId is required");

  const res = await authedFetch(`${API_BASE_URL}/user-moods/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(await readErr(res, "Get user mood failed"));

  const data = await res.json();
  return normalizeUserMood(data, id);
}

export async function createUserMood(payload) {
  const body = {
    name: (payload?.name || "").trim(),
    description: String(payload?.description ?? ""),
    prompt_fragment: String(payload?.prompt_fragment ?? ""),
    active: typeof payload?.active === "boolean" ? payload.active : true,
  };

  const res = await authedFetch(`${API_BASE_URL}/user-moods`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readErr(res, "Create user mood failed"));

  const data = await res.json();
  return normalizeUserMood(data);
}

export async function updateUserMood(docId, payload) {
  const id = String(docId || "").trim();
  if (!id) throw new Error("updateUserMood: docId is required");

  const body = {
    name: (payload?.name || "").trim(),
    description: String(payload?.description ?? ""),
    prompt_fragment: String(payload?.prompt_fragment ?? ""),
    active: typeof payload?.active === "boolean" ? payload.active : true,
  };

  const res = await authedFetch(`${API_BASE_URL}/user-moods/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readErr(res, "Update user mood failed"));

  const data = await res.json();
  return normalizeUserMood(data, id);
}

export async function listLlmTestCases({ page = 1, perPage = 20 } = {}) {
  const safePage = Math.max(1, Number(page || 1));
  const safePerPage = Math.min(200, Math.max(1, Number(perPage || 20)));

  const params = new URLSearchParams();
  params.set("page", String(safePage));
  params.set("per_page", String(safePerPage));

  const res = await authedFetch(`${API_BASE_URL}/llm-test-cases?${params.toString()}`);
  if (!res.ok) throw new Error(await readErr(res, "List LLM test cases failed"));

  const data = await res.json();
  const rawItems = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.runs)
        ? data.runs
        : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
            ? data.data
            : [];
  const items = rawItems.map((x) => normalizeLlmTestCase(x));

  const rawTotal = Number(
    data?.total ??
      data?.count ??
      data?.pagination?.total ??
      data?.meta?.total ??
      items.length
  );
  const total = Number.isFinite(rawTotal) ? rawTotal : items.length;

  const rawTotalPages = Number(
    data?.total_pages ??
      data?.totalPages ??
      data?.pages ??
      data?.pagination?.total_pages ??
      data?.meta?.total_pages ??
      (safePerPage > 0 ? Math.ceil(total / safePerPage) : 0)
  );
  const totalPages = Number.isFinite(rawTotalPages) && rawTotalPages > 0
    ? rawTotalPages
    : (safePerPage > 0 ? Math.max(1, Math.ceil(total / safePerPage)) : 1);

  return {
    items,
    page: Number(data?.page ?? data?.pagination?.page ?? safePage),
    perPage: Number(data?.per_page ?? data?.perPage ?? data?.pagination?.per_page ?? safePerPage),
    total,
    totalPages,
  };
}

export async function getLlmTestCase(docId) {
  const id = String(docId || "").trim();
  if (!id) throw new Error("getLlmTestCase: docId is required");

  const res = await authedFetch(`${API_BASE_URL}/llm-test-cases/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(await readErr(res, "Get LLM test case failed"));

  const data = await res.json();
  return normalizeLlmTestCase(data, id);
}

export async function createLlmTestCase(payload) {
  const body = {
    name: String(payload?.name ?? "").trim(),
    description: String(payload?.description ?? ""),
    assistant_scenario_id: String(payload?.assistant_scenario_id ?? "").trim(),
    client_id: String(payload?.client_id ?? "").trim(),
    user_scenario_id: String(payload?.user_scenario_id ?? "").trim(),
    user_mood_id: String(payload?.user_mood_id ?? "").trim(),
    goal: String(payload?.goal ?? ""),
    max_turns: Number(payload?.max_turns ?? 20),
    max_duration_sec: Number(payload?.max_duration_sec ?? 300),
    active: typeof payload?.active === "boolean" ? payload.active : true,
  };

  const res = await authedFetch(`${API_BASE_URL}/llm-test-cases`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readErr(res, "Create LLM test case failed"));

  const data = await res.json();
  return normalizeLlmTestCase(data);
}

export async function updateLlmTestCase(docId, payload) {
  const id = String(docId || "").trim();
  if (!id) throw new Error("updateLlmTestCase: docId is required");

  const body = {
    name: String(payload?.name ?? "").trim(),
    description: String(payload?.description ?? ""),
    assistant_scenario_id: String(payload?.assistant_scenario_id ?? "").trim(),
    client_id: String(payload?.client_id ?? "").trim(),
    user_scenario_id: String(payload?.user_scenario_id ?? "").trim(),
    user_mood_id: String(payload?.user_mood_id ?? "").trim(),
    goal: String(payload?.goal ?? ""),
    max_turns: Number(payload?.max_turns ?? 20),
    max_duration_sec: Number(payload?.max_duration_sec ?? 300),
    active: typeof payload?.active === "boolean" ? payload.active : true,
  };

  const res = await authedFetch(`${API_BASE_URL}/llm-test-cases/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readErr(res, "Update LLM test case failed"));

  const data = await res.json();
  return normalizeLlmTestCase(data, id);
}

export async function runLlmTestCase(testCaseId) {
  const id = String(testCaseId || "").trim();
  if (!id) throw new Error("runLlmTestCase: testCaseId is required");

  const res = await fetch(
    `${LLM_TESTS_API_BASE}/api/llm-tests/test-cases/${encodeURIComponent(id)}/run`,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "x-api-key": getLlmTestsApiKey(),
      },
    }
  );

  if (!res.ok) throw new Error(await readErr(res, "Run LLM test case failed"));

  try {
    return await res.json();
  } catch {
    return null;
  }
}

export async function runLlmTestCasesBulk(testCaseIds) {
  const ids = Array.from(
    new Set(
      (Array.isArray(testCaseIds) ? testCaseIds : [])
        .map((x) => String(x || "").trim())
        .filter(Boolean)
    )
  );

  if (ids.length === 0) {
    throw new Error("runLlmTestCasesBulk: at least one test case id is required");
  }

  const res = await fetch(`${LLM_TESTS_API_BASE}/api/llm-tests/test-cases/bulk-run`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "x-api-key": getLlmTestsApiKey(),
    },
    body: JSON.stringify({ test_case_ids: ids }),
  });

  if (!res.ok) throw new Error(await readErr(res, "Bulk run LLM test cases failed"));

  try {
    return await res.json();
  } catch {
    return null;
  }
}

export async function listLlmTestRuns({ page = 1, perPage = 20 } = {}) {
  const safePage = Math.max(1, Number(page || 1));
  const safePerPage = Math.min(200, Math.max(1, Number(perPage || 20)));

  const params = new URLSearchParams();
  params.set("page", String(safePage));
  params.set("per_page", String(safePerPage));
  params.set("include_full", "0");

  const res = await authedFetch(
    `${API_BASE_URL}/api/llm-tests/runs?${params.toString()}`,
    { method: "GET", headers: { Accept: "application/json" } }
  );
  if (!res.ok) throw new Error(await readErr(res, "List LLM test runs failed"));

  const data = await res.json();
  const rawItems = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(data?.runs)
        ? data.runs
        : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.data)
            ? data.data
            : [];

  const items = rawItems.map((x) => normalizeLlmTestRun(x));

  const rawTotal = Number(
    data?.total ??
      data?.count ??
      data?.pagination?.total ??
      data?.meta?.total ??
      items.length
  );
  const total = Number.isFinite(rawTotal) ? rawTotal : items.length;

  const rawTotalPages = Number(
    data?.total_pages ??
      data?.totalPages ??
      data?.pages ??
      data?.pagination?.total_pages ??
      data?.meta?.total_pages ??
      (safePerPage > 0 ? Math.ceil(total / safePerPage) : 0)
  );
  const totalPages = Number.isFinite(rawTotalPages) && rawTotalPages > 0
    ? rawTotalPages
    : (safePerPage > 0 ? Math.max(1, Math.ceil(total / safePerPage)) : 1);

  return {
    items,
    page: Number(data?.page ?? data?.pagination?.page ?? safePage),
    perPage: Number(data?.per_page ?? data?.perPage ?? data?.pagination?.per_page ?? safePerPage),
    total,
    totalPages,
  };
}

export async function getLlmTestRun(runId) {
  const id = String(runId || "").trim();
  if (!id) throw new Error("getLlmTestRun: runId is required");

  const res = await authedFetch(
    `${API_BASE_URL}/api/llm-tests/runs/${encodeURIComponent(id)}`,
    { method: "GET", headers: { Accept: "application/json" } }
  );
  if (!res.ok) throw new Error(await readErr(res, "Get LLM test run failed"));

  const data = await res.json();
  return normalizeLlmTestRun(data, id);
}

export async function convaiHistoryList({ page = 1, per_page = 20, q = null, client_id = null } = {}) {
  const params = new URLSearchParams();
  params.set('page', String(Math.max(1, Number(page || 1))));
  params.set('per_page', String(Math.min(200, Math.max(1, Number(per_page || 20)))));

  if (q != null && String(q).trim()) params.set('q', String(q).trim());
  if (client_id != null && String(client_id).trim()) params.set('client_id', String(client_id).trim());

  const res = await authedFetch(`${API_BASE_URL}/convai/history/list?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  });

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { msg = await res.text(); } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }

  return res.json();
}

function normalizeNotification(item) {
  const raw = item && typeof item === "object" ? item : {};
  const status = String(raw.status ?? "").trim().toLowerCase() === "read" ? "read" : "unread";

  return {
    id: String(raw.id ?? raw._id ?? raw.notification_id ?? raw.notificationId ?? "").trim(),
    dedupeKey: String(raw.dedupe_key ?? raw.dedupeKey ?? "").trim(),
    callId: String(raw.call_id ?? raw.callId ?? "").trim(),
    callState: String(raw.call_state ?? raw.callState ?? "").trim(),
    clientId: String(raw.client_id ?? raw.clientId ?? "").trim(),
    clientName: String(raw.client_name ?? raw.clientName ?? "").trim(),
    endReason: String(raw.end_reason ?? raw.endReason ?? "").trim(),
    eventType: String(raw.event_type ?? raw.eventType ?? "").trim(),
    message: String(raw.message ?? "").trim(),
    status,
    title: String(raw.title ?? "").trim(),
    to: String(raw.to ?? "").trim(),
    createdAt: String(raw.createdAt ?? raw.created_at ?? "").trim(),
    updatedAt: String(raw.updatedAt ?? raw.updated_at ?? "").trim(),
    readAt: String(raw.readAt ?? raw.read_at ?? "").trim(),
  };
}

function normalizeNotificationsResponse(data, { page, perPage }) {
  const rawItems = Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : [];
  const items = rawItems.map((item) => normalizeNotification(item));

  const total = Number(data?.total ?? items.length);
  const totalPages = Number(
    data?.total_pages ??
    data?.totalPages ??
    (perPage > 0 ? Math.max(1, Math.ceil((Number.isFinite(total) ? total : items.length) / perPage)) : 1)
  );
  const unreadTotal = Number(
    data?.unread_total ??
    data?.unreadTotal ??
    items.filter((item) => item.status === "unread").length
  );

  return {
    items,
    page: Number(data?.page ?? page),
    per_page: Number(data?.per_page ?? data?.perPage ?? perPage),
    total: Number.isFinite(total) ? total : items.length,
    total_pages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
    unread_total: Number.isFinite(unreadTotal) && unreadTotal >= 0
      ? unreadTotal
      : items.filter((item) => item.status === "unread").length,
  };
}

export async function listNotifications({ page = 1, per_page = 20, status = null } = {}) {
  const safePage = Math.max(1, Number(page || 1));
  const safePerPage = Math.min(200, Math.max(1, Number(per_page || 20)));
  const params = new URLSearchParams();
  params.set("page", String(safePage));
  params.set("per_page", String(safePerPage));

  if (status != null && String(status).trim()) {
    params.set("status", String(status).trim());
  }

  const res = await authedFetch(`${API_BASE_URL}/api/notifications?${params.toString()}`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    throw new Error(await readErr(res, `List notifications failed (${res.status})`));
  }

  const data = await res.json();
  return normalizeNotificationsResponse(data, { page: safePage, perPage: safePerPage });
}

export async function updateNotificationStatus(notificationId, status) {
  const id = String(notificationId || "").trim();
  if (!id) throw new Error("Missing notificationId");

  const nextStatus = String(status || "").trim().toLowerCase();
  if (nextStatus !== "read" && nextStatus !== "unread") {
    throw new Error("Invalid notification status");
  }

  const res = await authedFetch(`${API_BASE_URL}/api/notifications/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ status: nextStatus }),
  });
  if (!res.ok) {
    throw new Error(await readErr(res, `Update notification failed (${res.status})`));
  }

  try {
    const data = await res.json();
    return normalizeNotification(data);
  } catch {
    return null;
  }
}

export async function listCalls({ page = 1, per_page = 20, q = null, type = null } = {}) {
  const params = new URLSearchParams();
  params.set("page", String(Math.max(1, Number(page || 1))));
  params.set("per_page", String(Math.min(200, Math.max(1, Number(per_page || 20)))));

  if (q != null && String(q).trim()) params.set("q", String(q).trim());
  if (type != null && String(type).trim()) params.set("type", String(type).trim());

  const res = await authedFetch(`${API_BASE_URL}/api/calls?${params.toString()}`, {
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    throw new Error(await readErr(res, `List calls failed (${res.status})`));
  }

  return res.json();
}

export async function getCallById(call_id, { include_turns = 1 } = {}) {
  const id = String(call_id || "").trim();
  if (!id) throw new Error("Missing call_id");

  const params = new URLSearchParams();
  params.set("include_turns", Number(include_turns) === 1 ? "1" : "0");

  const res = await authedFetch(`${API_BASE_URL}/api/call/${encodeURIComponent(id)}?${params.toString()}`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(await readErr(res, `Get call failed (${res.status})`));

  return res.json();
}

export async function hangupCall(call_id) {
  const id = String(call_id || "").trim();
  if (!id) throw new Error("Missing call_id");

  const res = await authedFetch(`${API_BASE_URL}/api/call/${encodeURIComponent(id)}/hangup`, {
    method: "POST",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(await readErr(res, `Hangup call failed (${res.status})`));

  try {
    return await res.json();
  } catch {
    return null;
  }
}

export async function createCallSupervisorSession(call_id, { mode = "listen" } = {}) {
  const id = String(call_id || "").trim();
  if (!id) throw new Error("Missing call_id");

  const res = await authedFetch(`${API_BASE_URL}/api/call/${encodeURIComponent(id)}/supervisor/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ mode }),
  });
  if (!res.ok) throw new Error(await readErr(res, `Create supervisor session failed (${res.status})`));

  return res.json();
}

export async function createCallSupervisorOffer(call_id, { sdp, type = "offer", mode = "listen" } = {}) {
  const id = String(call_id || "").trim();
  if (!id) throw new Error("Missing call_id");
  if (!String(sdp || "").trim()) throw new Error("Missing SDP offer");

  const res = await authedFetch(`${API_BASE_URL}/api/call/${encodeURIComponent(id)}/supervisor/offer`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ sdp, type, mode }),
  });
  if (!res.ok) throw new Error(await readErr(res, `Create supervisor offer failed (${res.status})`));

  return res.json();
}

export async function getCallAudioSignedUrl(call_id) {
  const id = String(call_id || "").trim();
  if (!id) throw new Error("Missing call_id");

  const res = await authedFetch(`${API_BASE_URL}/api/audio/${encodeURIComponent(id)}`, {
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error(await readErr(res, `Get call audio URL failed (${res.status})`));

  return res.json();
}

export async function downloadCallAudio(call_id) {
  const id = String(call_id || "").trim();
  if (!id) throw new Error("Missing call_id");

  const res = await authedFetch(`${API_BASE_URL}/api/audio/${encodeURIComponent(id)}/download`, {
    headers: {
      Accept: "audio/*,application/octet-stream,application/json;q=0.9,*/*;q=0.8",
    },
  });

  if (!res.ok) {
    throw new Error(await readErr(res, `Download call audio failed (${res.status})`));
  }

  const blob = await res.blob();
  const filename = pickFilenameFromHeaders(res, `${id}.mp3`);

  return {
    blob,
    filename,
    contentType: blob.type || (res.headers.get("Content-Type") || ""),
  };
}

export async function getCallAudioFile(call_id) {
  const id = String(call_id || "").trim();
  if (!id) throw new Error("Missing call_id");

  const meta = await getCallAudioSignedUrl(id);
  const signedUrl = String(meta?.url || "").trim();
  if (!signedUrl) throw new Error("Audio URL is missing");

  const r2 = await fetch(signedUrl);
  if (!r2.ok) throw new Error(await readErr(r2, `Audio URL fetch failed (${r2.status})`));

  const blob = await r2.blob();
  const filename =
    (typeof meta?.filename === "string" && meta.filename.trim())
      ? meta.filename.trim()
      : pickFilenameFromHeaders(r2, `${id}.mp3`);

  return {
    blob,
    filename,
    contentType: blob.type || (r2.headers.get("Content-Type") || ""),
  };
}

export async function convaiHistoryGet(conversation_id) {
  const id = String(conversation_id || '').trim();
  if (!id) throw new Error('Missing conversation_id');

  const params = new URLSearchParams();
  params.set('conversation_id', id);

  const res = await authedFetch(`${API_BASE_URL}/convai/history?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  });

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { msg = await res.text(); } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }

  return res.json();
}

export async function convaiConversationGet(conversation_id) {
  const id = String(conversation_id || "").trim();
  if (!id) throw new Error("Missing conversation_id");

  const res = await authedFetch(
    `${API_BASE_URL}/convai/conversations/${encodeURIComponent(id)}`,
    { headers: { Accept: "application/json" } }
  );

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { msg = await res.text(); } catch {}
    throw new Error(msg || `HTTP ${res.status}`);
  }

  return res.json();
}


function pickFilenameFromHeaders(res, fallback) {
  const cd = res.headers.get("Content-Disposition") || res.headers.get("content-disposition") || "";
  const m = /filename\*?=(?:UTF-8''|")?([^";]+)/i.exec(cd);
  if (m?.[1]) {
    try { return decodeURIComponent(m[1]); } catch { return m[1]; }
  }
  return fallback;
}

export async function convaiConversationAudio(conversationId) {
  if (!conversationId) throw new Error("Missing conversationId");

  const url = `${API_BASE_URL}/convai/conversations/${encodeURIComponent(conversationId)}/audio`;
  const res = await authedFetch(url, {
    headers: { Accept: "audio/*,application/json;q=0.9,*/*;q=0.8" },
  });

  if (!res.ok) {
    let msg = `Audio fetch failed (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) msg = j.detail;
    } catch {
      try { msg = (await res.text()) || msg; } catch {}
    }
    throw new Error(msg);
  }

  const ct = (res.headers.get("Content-Type") || "").toLowerCase();


  if (ct.includes("application/json")) {
    const j = await res.json();
    const signed = j?.url || j?.signedUrl || j?.audio_url;
    if (!signed) throw new Error("Audio endpoint returned JSON without url");

    const r2 = await authedFetch(signed);
    if (!r2.ok) throw new Error(`Audio URL fetch failed (${r2.status})`);
    const blob2 = await r2.blob();

    const filename2 = pickFilenameFromHeaders(r2, `conversation_${conversationId}.mp3`);
    return { blob: blob2, filename: filename2, contentType: blob2.type || (r2.headers.get("Content-Type") || "") };
  }

  const blob = await res.blob();
  const filename = pickFilenameFromHeaders(res, `conversation_${conversationId}.mp3`);
  return { blob, filename, contentType: blob.type || (res.headers.get("Content-Type") || "") };
}

export async function startCall({
  phone,
  client_id,
  clientId,
  system_prompt = "",
  first_message = "",
  firstMessage = "",
} = {}) {
  const normalizedClientId = String(clientId || client_id || "").trim();
  const envType =
    typeof process.env.REACT_APP_ENVIRONMENT_DEV === "undefined"
      ? "dev"
      : ENVIRONMENT_DEV
        ? "dev"
        : "stage";
  const payload = {
    phone: String(phone || "").trim(),
    clientId: normalizedClientId,
    env_type: envType,
  };

  const prompt = String(system_prompt || "").trim();
  if (prompt) payload.system_prompt = prompt;
  const normalizedFirstMessage = String(first_message ?? firstMessage ?? "").trim();
  if (normalizedFirstMessage) payload.first_message = normalizedFirstMessage;

  const res = await authedFetch(`${API_BASE_URL}/api/calls/start-call`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    throw new Error(await readErr(res, `Start call failed (${res.status})`));
  }

  try {
    return await res.json();
  } catch {
    return null;
  }
}

function getElevenLabsApiKey() {
  return String(process.env.REACT_APP_ELEVENLABS_API_KEY || "").trim();
}

export function getElevenLabsAgentId() {
  return String(ELEVENLABS_AGENT_ID_DEFAULT || "").trim();
}

async function readElevenLabsErr(res, fallback) {
  let msg = fallback || `HTTP ${res.status}`;
  try {
    const j = await res.json();
    if (typeof j?.detail === "string" && j.detail.trim()) msg = j.detail;
    else if (typeof j?.message === "string" && j.message.trim()) msg = j.message;
    else if (j?.detail != null) msg = JSON.stringify(j.detail);
  } catch {
    try {
      const t = await res.text();
      if (t) msg = t;
    } catch {}
  }
  return msg;
}

async function elevenLabsFetch(path, { method = "GET", params = null, headers = {}, body } = {}) {
  const apiKey = getElevenLabsApiKey();
  if (!apiKey) {
    throw new Error("Липсва REACT_APP_ELEVENLABS_API_KEY в .env.local");
  }

  const qs = new URLSearchParams();
  if (params && typeof params === "object") {
    Object.entries(params).forEach(([k, v]) => {
      if (v == null) return;
      const s = String(v).trim();
      if (!s) return;
      qs.set(k, s);
    });
  }

  const url = `${ELEVENLABS_API_BASE}${path}${qs.toString() ? `?${qs.toString()}` : ""}`;

  return fetch(url, {
    method,
    headers: {
      "xi-api-key": apiKey,
      ...headers,
    },
    body,
  });
}

export async function elevenLabsConversationsList({
  agent_id = null,
  cursor = "",
  page_size = 20,
} = {}) {
  const agentId = String(agent_id || getElevenLabsAgentId()).trim();
  if (!agentId) throw new Error("Missing agent_id");

  const safePageSize = Math.min(100, Math.max(1, Number(page_size || 20)));
  const res = await elevenLabsFetch("/v1/convai/conversations", {
    method: "GET",
    headers: { Accept: "application/json" },
    params: {
      agent_id: agentId,
      cursor: String(cursor || "").trim(),
      page_size: String(safePageSize),
    },
  });

  if (!res.ok) {
    throw new Error(await readElevenLabsErr(res, `List conversations failed (${res.status})`));
  }

  const data = await res.json();
  return {
    conversations: Array.isArray(data?.conversations) ? data.conversations : [],
    has_more: Boolean(data?.has_more),
    next_cursor: String(data?.next_cursor || "").trim(),
    agent_id: agentId,
  };
}

export async function elevenLabsConversationGet(conversation_id) {
  const id = String(conversation_id || "").trim();
  if (!id) throw new Error("Missing conversation_id");

  const res = await elevenLabsFetch(`/v1/convai/conversations/${encodeURIComponent(id)}`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    throw new Error(await readElevenLabsErr(res, `Conversation fetch failed (${res.status})`));
  }

  return res.json();
}

export async function elevenLabsConversationAudio(conversationId) {
  const id = String(conversationId || "").trim();
  if (!id) throw new Error("Missing conversationId");

  const res = await elevenLabsFetch(`/v1/convai/conversations/${encodeURIComponent(id)}/audio`, {
    method: "GET",
    headers: {
      Accept: "audio/*,application/json;q=0.9,*/*;q=0.8",
    },
  });

  if (!res.ok) {
    throw new Error(await readElevenLabsErr(res, `Audio fetch failed (${res.status})`));
  }

  const ct = String(res.headers.get("Content-Type") || "").toLowerCase();

  if (ct.includes("application/json")) {
    const j = await res.json();
    const signed = j?.url || j?.audio_url || j?.signed_url || j?.signedUrl;
    if (!signed) throw new Error("Audio endpoint returned JSON without url");

    const r2 = await fetch(signed);
    if (!r2.ok) {
      throw new Error(await readElevenLabsErr(r2, `Audio URL fetch failed (${r2.status})`));
    }

    const blob2 = await r2.blob();
    const filename2 = pickFilenameFromHeaders(r2, `elevenlabs_${id}.mp3`);
    return {
      blob: blob2,
      filename: filename2,
      contentType: blob2.type || (r2.headers.get("Content-Type") || ""),
    };
  }

  const blob = await res.blob();
  const filename = pickFilenameFromHeaders(res, `elevenlabs_${id}.mp3`);
  return { blob, filename, contentType: blob.type || (res.headers.get("Content-Type") || "") };
}


export async function convaiOutbound(phone, system_prompt = "") {
  const payload = { phone: String(phone || "").trim() };
  const prompt = String(system_prompt || "").trim();
  if (prompt) payload.system_prompt = prompt;

  const res = await authedFetch(`${API_BASE_URL}/convai/outbound`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    let msg = `Outbound failed (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) msg = j.detail;
      else if (j?.message) msg = j.message;
    } catch {
      try {
        msg = await res.text();
      } catch {}
    }
    throw new Error(msg);
  }

  try {
    return await res.json();
  } catch {
    return null;
  }
}

export async function convaiOutboundWithApiKey(phone, system_prompt = "") {
  const payload = { phone: String(phone || "").trim() };
  const prompt = String(system_prompt || "").trim();
  if (prompt) payload.system_prompt = prompt;

  const res = await apiKeyFetch(`${API_BASE_URL}/convai/outbound`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    let msg = `Outbound failed (${res.status})`;
    try {
      const j = await res.json();
      if (j?.detail) msg = j.detail;
      else if (j?.message) msg = j.message;
    } catch {
      try {
        msg = await res.text();
      } catch {}
    }
    throw new Error(msg);
  }

  try {
    return await res.json();
  } catch {
    return null;
  }
}

